import { FormEvent, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useMutation } from "@apollo/client";
import {
  GET_COMMUNITY_POSTS,
  GET_COMMUNITY_POST,
  CREATE_COMMUNITY_REPLY,
  DELETE_COMMUNITY_POST,
} from "../../../api/community";
import { notify, notifyError } from "../../../utils/toast";
import { getErrorMessage } from "../../../utils/error";

type CommunityPost = {
  id: string;
  content: string;
  authorName: string;
  authorId?: number | null;
  status: string;
  createdAt: string;
  replyCount: number;
  replies: Array<{
    id: string;
    content: string;
    authorName: string;
    isAdmin: boolean;
    createdAt: string;
  }>;
};

const AdminCommunity: React.FC = () => {
  const { id } = useParams();
  const [selectedPostId, setSelectedPostId] = useState<string | null>(
    id || null
  );
  const [reply, setReply] = useState("");

  const {
    data: postsData,
    loading: postsLoading,
    refetch: refetchPosts,
  } = useQuery(GET_COMMUNITY_POSTS, {
    variables: { offset: 0, limit: 100 },
    skip: !!selectedPostId,
  });

  const { data: postData, refetch: refetchPost } = useQuery(
    GET_COMMUNITY_POST,
    {
      variables: { id: selectedPostId! },
      skip: !selectedPostId,
    }
  );

  const [createReply, { loading: creating }] = useMutation(
    CREATE_COMMUNITY_REPLY,
    {
      update: (cache, { data }, { variables }) => {
        if (!data?.createCommunityReply || !variables?.input) return;

        const newReply = data.createCommunityReply;
        const postId = variables.input.postId;

        // GET_COMMUNITY_POST 캐시 업데이트
        try {
          const existingPost = cache.readQuery<{
            communityPost: CommunityPost;
          }>({
            query: GET_COMMUNITY_POST,
            variables: { id: postId },
          });

          if (existingPost?.communityPost) {
            cache.writeQuery({
              query: GET_COMMUNITY_POST,
              variables: { id: postId },
              data: {
                communityPost: {
                  ...existingPost.communityPost,
                  replies: [...existingPost.communityPost.replies, newReply],
                  replyCount: existingPost.communityPost.replyCount + 1,
                  status: newReply.isAdmin
                    ? "ANSWERED"
                    : existingPost.communityPost.status,
                },
              },
            });
          }
        } catch (e) {
          console.error("Cache update error:", e);
        }

        // GET_COMMUNITY_POSTS 캐시 업데이트
        try {
          const existingPosts = cache.readQuery<{
            communityPosts: CommunityPost[];
          }>({
            query: GET_COMMUNITY_POSTS,
            variables: { offset: 0, limit: 100 },
          });

          if (existingPosts?.communityPosts) {
            const updatedPosts = existingPosts.communityPosts.map((p) => {
              if (p.id === postId) {
                return {
                  ...p,
                  replies: [...(p.replies || []), newReply],
                  replyCount: p.replyCount + 1,
                  status: newReply.isAdmin ? "ANSWERED" : p.status,
                };
              }
              return p;
            });

            cache.writeQuery({
              query: GET_COMMUNITY_POSTS,
              variables: { offset: 0, limit: 100 },
              data: {
                communityPosts: updatedPosts,
              },
            });
          }
        } catch (e) {
          console.error("Cache update error:", e);
        }
      },
      onCompleted: () => {
        setReply("");
        notify("답변이 등록되었습니다!");
        // 캐시가 이미 업데이트되었으므로 refetch는 선택사항
        refetchPost();
        refetchPosts();
      },
      onError: (error) => {
        console.error("답변 작성 실패:", error);
        const errorMessage = getErrorMessage(error);
        notifyError(`답변 작성 실패: ${errorMessage}`);
      },
    }
  );

  const [deletePost, { loading: deleting }] = useMutation(
    DELETE_COMMUNITY_POST,
    {
      onCompleted: (data) => {
        if (data.deleteCommunityPost.ok) {
          notify("게시글이 삭제되었습니다.");
          if (selectedPostId) {
            setSelectedPostId(null);
          }
          refetchPosts();
        } else {
          notifyError(
            data.deleteCommunityPost.error || "게시글 삭제에 실패했습니다."
          );
        }
      },
      onError: (error) => {
        console.error("게시글 삭제 실패:", error);
        const errorMessage = getErrorMessage(error);
        notifyError(`게시글 삭제 실패: ${errorMessage}`);
      },
    }
  );

  const handleDeletePost = (postId: string) => {
    if (!window.confirm("정말로 이 게시글을 삭제하시겠습니까?")) {
      return;
    }
    deletePost({
      variables: {
        id: parseInt(postId, 10),
      },
    });
  };

  const posts: CommunityPost[] = useMemo(
    () => postsData?.communityPosts || [],
    [postsData?.communityPosts]
  );
  const post: CommunityPost | undefined = postData?.communityPost;
  const canSubmit = useMemo(
    () => reply.trim().length > 0 && !creating && !!selectedPostId,
    [reply, creating, selectedPostId]
  );

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!selectedPostId || !canSubmit) return;
    createReply({
      variables: {
        input: {
          postId: selectedPostId,
          content: reply.trim(),
        },
      },
    });
  };

  if (selectedPostId && post) {
    return (
      <div className="px-4 py-5">
        <button
          className="text-sm mb-3"
          onClick={() => {
            setSelectedPostId(null);
            setReply("");
          }}
        >
          ← 목록으로
        </button>
        <h1 className="text-lg font-semibold mb-4">피드백 답변</h1>

        <div className="border rounded-lg p-4 mb-6 bg-white">
          <div className="flex items-start justify-between mb-2">
            <div className="text-xs text-gray-500">
              {post.authorName} · {new Date(post.createdAt).toLocaleString()}
            </div>
            <button
              onClick={() => handleDeletePost(post.id)}
              disabled={deleting}
              className="text-xs text-red-600 hover:text-red-700 px-2 py-1 rounded hover:bg-red-50 disabled:opacity-50"
            >
              {deleting ? "삭제 중..." : "삭제"}
            </button>
          </div>
          <p className="text-sm whitespace-pre-wrap">{post.content}</p>
        </div>

        <div className="mb-6">
          <h2 className="text-sm font-medium mb-3">
            기존 답변 ({post.replies.length})
          </h2>
          {post.replies.length === 0 ? (
            <div className="text-sm text-gray-500">아직 답변이 없습니다.</div>
          ) : (
            <ul className="space-y-3">
              {post.replies.map((r) => (
                <li
                  key={r.id}
                  className={`border rounded-md p-3 ${
                    r.isAdmin
                      ? "bg-[#F4F5F7] border-[#1562fc]"
                      : "bg-white border-[#F4F5F7]"
                  }`}
                >
                  <div className="text-xs text-gray-500">
                    {r.isAdmin ? (
                      <span className="text-[#1562fc] font-medium">운영자</span>
                    ) : (
                      r.authorName
                    )}{" "}
                    · {new Date(r.createdAt).toLocaleString()}
                  </div>
                  <div className="text-sm mt-1 whitespace-pre-wrap">
                    {r.content}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <form onSubmit={onSubmit} className="border-t pt-6">
          <div className="mb-3">
            <label className="text-sm font-medium mb-2 block">
              운영자 답변 작성
            </label>
            <textarea
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              placeholder="답변을 입력하세요"
              rows={6}
              className="w-full border border-[#F4F5F7] rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#1562fc]"
              disabled={creating}
            />
          </div>
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={!canSubmit}
              className={`px-4 py-2 rounded-md text-sm ${
                canSubmit
                  ? "bg-[#1562fc] text-white hover:bg-[#124ab7]"
                  : "bg-[#F4F5F7] text-[#BBBDC0]"
              }`}
            >
              {creating ? "등록 중..." : "답변 등록"}
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="px-4 py-5">
      <h1 className="text-lg font-semibold mb-4">커뮤니티 피드백 관리</h1>

      {postsLoading ? (
        <div className="text-sm text-gray-500">로딩 중...</div>
      ) : posts.length === 0 ? (
        <div className="text-sm text-gray-500">아직 게시글이 없습니다.</div>
      ) : (
        <ul className="space-y-3">
          {posts.map((p) => {
            const hasReply = p.replyCount > 0;
            const hasAdminReply = p.replies?.some((r) => r.isAdmin) || false;
            const status = p.status?.toUpperCase();
            const isAnswered = status === "ANSWERED";
            return (
              <li key={p.id}>
                <div
                  className={`w-full text-left border rounded-lg p-3 transition-colors ${
                    hasReply
                      ? isAnswered || hasAdminReply
                        ? "bg-[#F4F5F7] border-[#1562fc]"
                        : "bg-[#F4F5F7] border-[#F4F5F7]"
                      : "bg-white border-[#1562fc]"
                  }`}
                >
                  <button
                    className="w-full text-left"
                    onClick={() => setSelectedPostId(p.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="text-xs text-gray-500">
                        {p.authorName} ·{" "}
                        {new Date(p.createdAt).toLocaleString()}
                      </div>
                      {hasReply && (
                        <span
                          className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                            isAnswered || hasAdminReply
                              ? "bg-[#1562fc] text-white"
                              : "bg-[#F4F5F7] text-[#1562fc]"
                          }`}
                        >
                          {isAnswered || hasAdminReply
                            ? "✓ 답변 완료"
                            : `${p.replyCount}개 답변`}
                        </span>
                      )}
                      {!hasReply && (
                        <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-red-100 text-red-700">
                          답변 필요
                        </span>
                      )}
                    </div>
                    {p.content && (
                      <p className="text-sm text-gray-700 mt-2 line-clamp-2">
                        {p.content}
                      </p>
                    )}
                  </button>
                  <div className="flex justify-end mt-2 pt-2 border-t border-gray-100">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeletePost(p.id);
                      }}
                      disabled={deleting}
                      className="text-xs text-red-600 hover:text-red-700 px-2 py-1 rounded hover:bg-red-50 disabled:opacity-50"
                    >
                      {deleting ? "삭제 중..." : "삭제"}
                    </button>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default AdminCommunity;
